import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, FilterCard, Field, SimpleSelect, EmptyState } from "@/components/common";
import { fmtPct, statusBg, MONTHS } from "@/lib/helpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BarChart3, AlertTriangle } from "lucide-react";
import { useIsNarrow, lineChartMargin, capaianLabels, targetEndLabel } from "@/components/chartLabels";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

export default function DashboardDepartemen() {
  const { indikators, tahuns } = useMeta();
  const [indikator, setIndikator] = useState("");
  const [tahun, setTahun] = useState("");
  const [departemen, setDepartemen] = useState("");
  const [deps, setDeps] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { if (!indikator && indikators.length) setIndikator(indikators[0].kode); }, [indikators]);
  useEffect(() => { if (!tahun && tahuns.length) setTahun(String(tahuns[0])); }, [tahuns]);
  useEffect(() => {
    if (indikator) api.get(`/departemen?indikator=${encodeURIComponent(indikator)}`).then((r) => {
      setDeps(r.data);
      if (r.data.length && !r.data.includes(departemen)) setDepartemen(r.data[0]);
    });
  }, [indikator]);

  useEffect(() => {
    if (!indikator || !tahun || !departemen) return;
    api.get(`/dashboard/departemen?indikator=${encodeURIComponent(indikator)}&tahun=${tahun}&departemen=${encodeURIComponent(departemen)}`).then((r) => setData(r.data));
  }, [indikator, tahun, departemen]);

  const bulanAda = data?.bulan_ada || [];
  const narrow = useIsNarrow();
  const chartData = bulanAda.map((b) => ({
    name: MONTHS[b - 1].short,
    Capaian: data.rata_per_bulan[b] != null ? +(data.rata_per_bulan[b] * 100).toFixed(1) : null,
    Target: data.target_dept != null ? +(data.target_dept * 100).toFixed(1) : null,
  }));

  return (
    <div data-testid="dashboard-departemen-page">
      <PageHeader icon={BarChart3} title="Dashboard Departemen" subtitle="Format identik sheet Dashboard Excel: Target, Capaian tiap bulan, dan Rata-rata di bawah. Ganti dropdown, data langsung berubah." />

      <FilterCard>
        <Field label="Indikator">
          <SimpleSelect testId="dd-indikator-select" value={indikator} onChange={setIndikator}
            options={indikators.map((i) => ({ label: i.kode, value: i.kode }))} className="w-[220px]" />
        </Field>
        <Field label="Tahun">
          <SimpleSelect testId="dd-tahun-select" value={tahun} onChange={setTahun}
            options={tahuns.map((t) => ({ label: String(t), value: t }))} className="w-[120px]" />
        </Field>
        <Field label="Departemen">
          <SimpleSelect testId="dd-departemen-select" value={departemen} onChange={setDepartemen}
            options={deps.map((d) => ({ label: d, value: d }))} placeholder="Pilih departemen" className="w-[240px]" />
        </Field>
      </FilterCard>

      {data && !data.has_target && (
        <Alert className="mb-6 border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Target tahun ini belum diisi</AlertTitle>
          <AlertDescription className="text-amber-700">Isi target di halaman Kelola Target Tahunan.</AlertDescription>
        </Alert>
      )}

      {!data || bulanAda.length === 0 ? (
        <EmptyState icon={BarChart3} title="Belum ada data" desc="Belum ada capaian untuk departemen ini." />
      ) : (
        <div className="grid grid-cols-1 gap-6">
          <Card className="border-slate-200 shadow-sm ">
            <CardHeader><CardTitle className="text-lg">Grafik — {departemen}</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: "100%", height: 340 }} data-testid="dd-chart">
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={lineChartMargin(narrow, -10)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} domain={[0, 100]} unit="%" />
                    <Tooltip formatter={(v) => (v != null ? `${v}%` : "N/A")} />
                    <Legend />
                    <Line type="monotone" dataKey="Target" stroke="hsl(var(--brand-lime))" strokeWidth={3} strokeDasharray="6 4" dot={false} connectNulls>
                    {targetEndLabel({ data: chartData, narrow })}
                  </Line>
                    <Line type="monotone" dataKey="Capaian" stroke="hsl(var(--brand-teal))" strokeWidth={3} dot={{ r: 4 }} connectNulls>
                    {capaianLabels({ data: chartData, narrow })}
                  </Line>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm ">
            <CardHeader><CardTitle className="text-lg">Tabel Capaian — {departemen} ({tahun})</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full min-w-[860px] table-fixed border-collapse text-sm" data-testid="dd-table">
                  <colgroup>
                    <col style={{ width: 200 }} />
                    <col style={{ width: 64 }} />
                    {bulanAda.map((b) => (<col key={b} />))}
                  </colgroup>
                  <thead>
                    <tr className="bg-primary text-primary-foreground">
                      <th className="px-3 py-2.5 text-left sticky left-0 z-10 bg-primary">Poliklinik</th>
                      <th className="px-2 py-2.5 text-center">Target</th>
                      {bulanAda.map((b) => (<th key={b} className="px-1 py-2.5 text-center">{MONTHS[b - 1].short}</th>))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.poliklinik.map((p) => (
                      <tr key={p.poliklinik_id} className="border-b border-slate-100" data-testid={`dd-row-${p.poliklinik_id}`}>
                        <td className="px-3 py-1.5 font-medium text-slate-700 sticky left-0 z-10 bg-white break-words">{p.nama_poliklinik}</td>
                        <td className="px-2 py-1.5 text-center text-xs tabular-nums text-slate-500">{fmtPct(p.target)}</td>
                        {bulanAda.map((b) => (
                          <td key={b} className={`px-1 py-1.5 text-center text-xs tabular-nums ${p.bulan[b] != null ? statusBg(p.bulan[b], p.target) : "text-slate-300"}`}>{fmtPct(p.bulan[b])}</td>
                        ))}
                      </tr>
                    ))}
                    <tr className="bg-brand-mist font-bold border-t-2 border-brand-sky/60">
                      <td className="px-3 py-2 sticky left-0 z-10 bg-brand-mist text-brand-deep">Rata-rata</td>
                      <td className="px-2 py-2 text-center text-xs tabular-nums text-brand-deep">{fmtPct(data.target_dept)}</td>
                      {bulanAda.map((b) => (
                        <td key={b} className="px-2 py-2 text-center text-xs tabular-nums text-brand-deep">{fmtPct(data.rata_per_bulan[b])}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
